<script>
import { AbcDropdown, AbcToggle } from "@ordbogen-npm/abc-dsk";
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import TgSavedToast from "@/abc-dsk/components/SavedToast/TgSavedToast.vue";
import { PACKAGE_GROUP_ORDBOGEN } from "@/common/package-groups-types";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useLanguageLevelStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default defineComponent({
  name: "OrdbogenSettings",
  components: {
    AbcDropdown,
    AbcToggle,
    TgSavedToast
  },
  data() {
    return {
      dictionaryLevelLabels: this.$tm("levels"),
      wordclassLevelLabels: this.$tm("settings.ordbogen.wordclassLevelLabels"),
      autoExpandArticlesLabels: this.$tm("settings.ordbogen.autoExpandArticlesLabels"),
      updatedDictionaryLevelToast: false,
      updatedWordclassLevelToast: false,
      updatedAutoExpandArticlesToast: false
    };
  },
  computed: {
    ...mapStores(useAuthStore, useLanguageLevelStore, useSettingsStore),

    packageGroups() {
      return this.settingsStore.packageGroups;
    },

    wordclassLevel() {
      return this.settingsStore.userPreferences.ordbogenWordclassLevel;
    },

    dictionaryLevel() {
      return this["language-levelStore"].ordbogenLanguageLevel;
    },

    dictionaryLevelOptions() {
      return this.makeOptions(this.dictionaryLevelLabels);
    },

    wordclassOptions() {
      return this.makeOptions(this.wordclassLevelLabels);
    },

    autoExpandArticles() {
      return this.settingsStore.userPreferences.ordbogenArticleTranslationsExpandedDefault;
    },

    autoExpandArticlesSubheading() {
      return this.$rt(this.autoExpandArticlesLabels[this.authStore.user.level]);
    }
  },
  methods: {
    async setDictionaryLevel(level) {
      this.updatedDictionaryLevelToast = false;

      await useLanguageLevelStore().updateAllLanguageLevels({
        level,
        packageGroup: PACKAGE_GROUP_ORDBOGEN
      });

      this.updatedDictionaryLevelToast = true;

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.ClickAbcSettings,
        `Changed dictionary level to ${level}`
      );
    },

    async setWordclassLevel(wordclassLevel) {
      this.updatedWordclassLevelToast = false;

      await this.settingsStore.setOrdbogenWordClassLevel(wordclassLevel);

      this.updatedWordclassLevelToast = true;

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.ClickAbcSettings,
        `Changed grammar terms to ${wordclassLevel}`
      );
    },

    async toggleAutoExpandArticles(value) {
      this.updatedAutoExpandArticlesToast = false;

      await this.settingsStore.setOrdbogenArticleTranslationsExpandedDefault(value);

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.ClickAbcSettings,
        `Toggled auto unfold to ${value}`
      );

      this.updatedAutoExpandArticlesToast = true;
    },

    makeOptions(labels) {
      return Object.keys(labels).map((level) => {
        const recommendStr =
          this.authStore.user.level === level ? this.$t("settings.ordbogen.recommendedSuffix") : "";

        return {
          label: this.$rt(labels[level]) + recommendStr,
          value: level
        };
      });
    }
  }
});
</script>

<template>
  <div>
    <div class="d-none d-lg-block settings-group">
      <h2 class="settings-group__heading settings-group__heading--big">
        {{ $t("settings.ordbogen.title") }}
      </h2>
      <p class="settings-group__subheading mb-0">
        {{ $t("settings.ordbogen.description") }}
      </p>
    </div>
    <hr class="settings-group-divider d-none d-lg-block" />
    <div class="d-block d-lg-none settings-group settings-group--even-spacing pb-0 row">
      <div class="col">
        <p class="settings-group__subheading mb-n2">
          {{ $t("settings.ordbogen.description") }}
        </p>
      </div>
    </div>
    <div class="settings-group row">
      <div class="col-12 settings-group__content">
        <div class="settings-group__details">
          <h3 class="settings-group__heading">
            {{ $t("settings.ordbogen.dictionaryLevelHeading") }}
          </h3>
          <p class="settings-group__subheading">
            {{ $t("settings.ordbogen.dictionaryLevelSubheading") }}
          </p>
        </div>
      </div>
      <div class="col-12">
        <div class="settings-group__toast-container-vertical">
          <AbcDropdown
            :model-value="dictionaryLevel"
            :options="dictionaryLevelOptions"
            :aria-label="$t('settings.ordbogen.dictionaryLevelHeading')"
            @change="setDictionaryLevel($event)"
          />
          <TgSavedToast
            v-model:show="updatedDictionaryLevelToast"
            class="ml-2 mt-2 mb-n2 ml-lg-3 mb-lg-0 mt-lg-0"
          />
        </div>
      </div>
    </div>
    <hr class="settings-group-divider" />
    <div class="settings-group row">
      <div class="col-12 settings-group__content">
        <div class="settings-group__details">
          <h3 class="settings-group__heading">
            {{ $t("settings.ordbogen.wordclassLevelHeading") }}
          </h3>
          <p class="settings-group__subheading">
            {{ $t("settings.ordbogen.wordclassLevelSubheading") }}
          </p>
        </div>
      </div>
      <div class="col-12">
        <div class="settings-group__toast-container-vertical">
          <AbcDropdown
            :model-value="wordclassLevel"
            :options="wordclassOptions"
            :aria-label="$t('settings.ordbogen.wordclassLevelHeading')"
            @change="setWordclassLevel($event)"
          />
          <TgSavedToast
            v-model:show="updatedWordclassLevelToast"
            class="ml-2 mt-2 mb-n2 ml-lg-3 mb-lg-0 mt-lg-0"
          />
        </div>
      </div>
    </div>
    <hr class="settings-group-divider" />
    <div class="settings-group row">
      <div class="col-12 settings-group__content">
        <div class="settings-group__details">
          <h3 class="settings-group__heading">
            {{ $t("settings.ordbogen.autoExpandArticlesHeading") }}
          </h3>
          <p class="settings-group__subheading">{{ autoExpandArticlesSubheading }}</p>
        </div>
      </div>
      <div class="col-12 mb-3">
        <div class="settings-group__toast-container settings-group__toast-container--centered">
          <AbcToggle
            :model-value="autoExpandArticles"
            :aria-label="$t('settings.ordbogen.autoExpandArticlesHeading')"
            @update:model-value="toggleAutoExpandArticles($event)"
          />
          <TgSavedToast
            v-model:show="updatedAutoExpandArticlesToast"
            :top="0"
            :left="60"
            absolute
          />
        </div>
      </div>
    </div>
  </div>
</template>
